import { createContext } from 'react';
import { IntlProvider } from 'react-intl';
import '../theme/mui-augmentations';

import en from '../i18n/en.json';
import fr from '../i18n/fr.json';
import it from '../i18n/it.json';
import { ThemeProvider } from '../theme';
import { DatePickerProvider } from './DatePickerProvider';

// Import messages
const messages = {
  en: en,
  fr: fr,
  it: it
};

const dsContext = createContext({ currency: 'EUR' });
const { Provider } = dsContext;

const LOCALES = ['en', 'fr', 'it'] as const;
const CURRENCIES = ['EUR', 'USD'] as const;
const DEFAULT_LOCALE = 'en';
const DEFAULT_CURRENCY = 'EUR';

type AvailableLocales = (typeof LOCALES)[number];
type AvailableCurrencies = (typeof CURRENCIES)[number];

type DesignSystemProps = {
  /**
   * Children component
   */
  children: React.ReactNode;
  /**
   * Language to use for i18n
   */
  locale?: AvailableLocales;
  /**
   * Currency returned by useCurrency
   */
  currency?: AvailableCurrencies;
};

/**
 * Provides all the features and contexts the Design System needs.
 * - Global Provider to share some data.
 * - IntlProvider for i18n.
 * - ThemeProvider for theming with Mui.
 */
const DesignSystemProvider = ({
  children,
  locale = DEFAULT_LOCALE,
  currency = DEFAULT_CURRENCY
}: DesignSystemProps) => {
  // If wrong locale, set fallback
  if (!LOCALES.includes(locale)) {
    // eslint-disable-next-line no-console
    console.warn(`WARNING: Locale ${locale} is not available.`);
    locale = DEFAULT_LOCALE;
  }

  // If wrong currency, set fallback
  if (!CURRENCIES.includes(currency)) {
    // eslint-disable-next-line no-console
    console.warn(`WARNING: Currency ${currency} is not available.`);
    currency = DEFAULT_CURRENCY;
  }

  return (
    <Provider value={{ currency: currency }}>
      <IntlProvider
        locale={locale}
        key={locale}
        messages={{ ...messages[DEFAULT_LOCALE], ...messages[locale] }}
        defaultLocale={DEFAULT_LOCALE}
      >
        <DatePickerProvider locale={locale}>
          <ThemeProvider locale={locale}>{children}</ThemeProvider>
        </DatePickerProvider>
      </IntlProvider>
    </Provider>
  );
};

export { dsContext, DesignSystemProvider };
